import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output, PLATFORM_ID, ViewChild } from '@angular/core';
import { Icons } from '../../../../shared/helpers/icons';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { LucideAngularModule } from 'lucide-angular';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { fileUploadValidator } from '../../../../shared/helpers/fileUploadValidation';
import { DateUtils } from '../../../../shared/helpers/dateUtils';
import { EScheduleTypes } from '../../../../shared/models/eop_types.enum';
import { UserDataService } from '../../../../shared/services/userData.service';
import { ScheduleService } from '../../../../shared/services/schedule.service';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { NoEmojiDirective } from '../../../../shared/helpers/noEmoji.directive';
declare var initFlowbite: any;

@Component({
  selector: 'new-event',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    ReactiveFormsModule,
    SpinnerComponent,
    NoEmojiDirective
  ],
  templateUrl: './new-event.component.html',
  styleUrl: './new-event.component.css'
})
export class NewEventComponent implements AfterViewInit, OnInit {
  @ViewChild('datepickerStartInput') datepickerStartInput!: ElementRef;
  // @ViewChild('datepickerEndInput') datepickerEndInput!: ElementRef;
  
  icons = Icons;
  scheduleTypes = EScheduleTypes;
  currentBuildingId: string | null = null;

  loading: boolean = false;

  @Input() selectedDate: string | null = null;

  @Output() onClose = new EventEmitter<void>();
  @Output() onSave = new EventEmitter<any>();

  eventForm = new FormGroup({
    name: new FormControl('', Validators.required),
    type_id: new FormControl(EScheduleTypes.Training, Validators.required), // Assuming Training- 1 is the default type ID
    start_date: new FormControl('', Validators.required),
    time: new FormControl('08:00', Validators.required),
    duration: new FormControl('1 hour', Validators.required),
    details: new FormControl('', Validators.required),
    files: new FormControl<File[]>([], fileUploadValidator())
  })

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    private userDataService: UserDataService,
    private scheduleService: ScheduleService
  ) {}

  ngOnInit(): void {
    if (this.selectedDate) {
      this.eventForm.get('start_date')?.setValue(this.selectedDate);
    }
    this.currentBuildingId = this.userDataService.getBuildingId();
  }
  
  async ngAfterViewInit(): Promise<void> {
    if (isPlatformBrowser(this.platformId)) {

      // Dynamically import the Datepicker to avoid server-side loading
      const { Datepicker } = await import('flowbite-datepicker');
      if (this.datepickerStartInput) {
        new Datepicker(this.datepickerStartInput.nativeElement, {
          autohide: true,
          format: 'mm/dd/yyyy'
        });
        if (this.selectedDate) { // Check if selectedDate is set, make it a default value for start date
          this.datepickerStartInput.nativeElement.value = DateUtils.formatDateForDisplay(this.selectedDate);
        }
        // Add event listener for date change
        // This will update the form control with the formatted date when the date is changed
        this.datepickerStartInput.nativeElement.addEventListener('changeDate', (event: any) => {
          const formattedDate = DateUtils.formatDateForBackend(event.target.value);
          this.eventForm.get('start_date')?.setValue(formattedDate);
        });
      }
      // if (this.datepickerEndInput) {
      //   new Datepicker(this.datepickerEndInput.nativeElement, {
      //     autohide: true,
      //     format: 'mm/dd/yyyy'
      //   });
      //   if (this.selectedDate) {
      //     this.datepickerEndInput.nativeElement.value = DateUtils.formatDateForDisplay(this.selectedDate);
      //   }
      //   this.datepickerEndInput.nativeElement.addEventListener('changeDate', (event: any) => {
      //     const formattedDate = DateUtils.formatDateForBackend(event.target.value);
      //     this.eventForm.get('end_date')?.setValue(formattedDate);
      //   });
      // }
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const files = Array.from(input.files);
      this.eventForm.get('files')?.setValue(files);
      this.eventForm.get('files')?.markAsDirty();
      this.eventForm.get('files')?.updateValueAndValidity();
    }
  }

  saveNew(): void {
    if (this.eventForm.valid && this.currentBuildingId) {
      this.loading = true;
      const eventForm = new FormData();
  
      // Convert the form group to FormData
      Object.keys(this.eventForm.controls).forEach((key) => {
        const controlValue = this.eventForm.get(key)?.value;
    
        if (key === 'files') {
            if (controlValue && controlValue.length > 0) {
                // Handle file upload for 'files' field
                controlValue.forEach((file: File) => {
                    if (file instanceof Blob) {
                        eventForm.append(key, file, file.name); // Append each file
                    } else {
                        console.error("Invalid file type detected:", file);
                    }
                });
            }
        // If no files are present, do not append anything for 'files'.
        } else {
            // Add other form fields (non-file fields)
            if (controlValue !== undefined && controlValue !== null) {
                eventForm.append(key, controlValue);
            } else {
                // If the field is null or undefined, append an empty string to match FastAPI's expectation
                eventForm.append(key, '');
            }
        }
    });
  
      this.scheduleService.createNewSchedule(this.currentBuildingId, eventForm).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.onSave.emit({ type: 'success', message: 'This occurred after creating a new event.' });
          }
          console.log(response);
        },
        error: (error) => {
          this.loading = false;
          this.onSave.emit({ type: 'unsuccess', message: error.message });
        }
      })
    } 
  }

  closeCreate(): void {
    this.onClose.emit();
  }

  // validateDateRange(): void {
  //   const start = this.eventForm.get('start_date')?.value;
  //   const end = this.eventForm.get('end_date')?.value;
  
  //   if (start && end && new Date(start) > new Date(end)) {
  //     alert('End date must be after the start date.');
  //     this.eventForm.get('end_date')?.setErrors({ invalidRange: true });
  //   } else {
  //     this.eventForm.get('end_date')?.setErrors(null);
  //   }
  // }

}
